import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { FiImage, FiTrash2, FiUploadCloud } from 'react-icons/fi';

import ColorPicker from '@/components/ui/color-picker/ColorPicker';
import { ThemeTypes } from '@/components/ui/color-picker/utils/enums';
import {
	ChatbotThemeColors,
	ChatbotThemeGradients,
	defaultAvatars,
} from '@/components/utils/constants';
import { getImageNameFromUrl } from '@/components/utils/utils';
import { useUserStore } from '@/hooks/useUserStore';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const CustomizeChatbot: React.FC = () => {
	const { store, refetch } = useUserStore();

	const [theme, setTheme] = useState({ type: ThemeTypes.Color, color: '' });

	const [logoFile, setLogoFile] = useState<File | null>(null);
	const [avatar, setAvatar] = useState<{
		defaultUrl: string | null;
		customFile: File | null;
	}>({
		defaultUrl: null,
		customFile: null,
	});
	const [deletionStatus, setDeletionStatus] = useState({
		logo: false,
		avatar: false,
	});

	const logoFileName =
		logoFile?.name ||
		(store?.businessLogoUrl &&
			!deletionStatus.logo &&
			getImageNameFromUrl(store.businessLogoUrl)) ||
		'Select and upload the files of your choice';

	const avatarFileName =
		avatar.customFile?.name ||
		(store?.avatarUrl &&
			!avatar.defaultUrl &&
			!deletionStatus.avatar &&
			getImageNameFromUrl(store.avatarUrl)) ||
		'No selected File';

	useEffect(() => {
		if (store?.theme && store?.themeType) {
			const isValidTheme =
				(store.themeType === ThemeTypes.Color &&
					ChatbotThemeColors.some((c) => c.name === store.theme)) ||
				(store.themeType === ThemeTypes.Gradient &&
					ChatbotThemeGradients.some((g) => g.name === store.theme)) ||
				store.themeType === ThemeTypes.Custom;

			if (isValidTheme) {
				setTheme({ type: store.themeType as ThemeTypes, color: store.theme });
			}
		}

		if (store?.avatarUrl && defaultAvatars.includes(store.avatarUrl)) {
			setAvatar({ defaultUrl: store.avatarUrl, customFile: null });
		}
	}, [store]);

	const { mutate: updateChatbotSettings, isPending } = useMutation({
		mutationFn: () => {
			if (!store?.id) throw new Error('Store not found');

			return haggleApiService.updateChatbotSettings(store.id, {
				themeType: theme.type,
				theme: theme.color,
				businessLogo: deletionStatus.logo ? null : logoFile,
				avatar: deletionStatus.avatar ? null : avatar.customFile,
				avatarUrl:
					deletionStatus.avatar || avatar.customFile
						? undefined
						: avatar.defaultUrl ?? undefined,
				resetLogo: deletionStatus.logo,
				resetAvatar: deletionStatus.avatar,
			});
		},
		onSuccess: () => {
			refetch();
			notificationService.success('Chatbot settings updated successfully!');
		},
		onError: (error: any) => {
			notificationService.error(
				error?.response?.data?.message || 'Update failed',
			);
		},
	});

	const handleSave = () => {
		if (!store?.id) {
			notificationService.error('Store not found');
			return;
		}
		updateChatbotSettings();
	};

	const handleLogoDelete = () => {
		setLogoFile(null);
		setDeletionStatus((prev) => ({ ...prev, logo: true }));
	};

	const handleAvatarDelete = () => {
		setAvatar({ defaultUrl: null, customFile: null });
		setDeletionStatus((prev) => ({ ...prev, avatar: true }));
	};

	return (
		<div className="max-w-2xl mx-auto bg-white rounded-xl border-2 p-6">
			<h2 className="text-2xl font-semibold mb-1">Customize Chatbot</h2>
			<p className="text-gray-500 mb-6">Customize your chatbot</p>

			{/* Logo Upload */}
			<div className="mb-8">
				<h3 className="font-medium mb-2">Insert Business Logo</h3>
				<div className="flex items-center gap-3">
					<label className="flex items-center gap-4 cursor-pointer">
						<div className="flex items-center justify-center w-12 h-12 rounded-full border border-gray-300 text-gray-500">
							<FiUploadCloud size={24} />
						</div>
						<div>
							<p className="text-sm font-medium text-gray-700">Upload files</p>
							<p className="text-xs text-gray-400">{logoFileName}</p>
						</div>
						<input
							type="file"
							className="hidden"
							onChange={(e) => {
								setLogoFile(e.target.files?.[0] || null);
								setDeletionStatus((prev) => ({ ...prev, logo: false }));
							}}
						/>
					</label>
					{(logoFile || (store?.businessLogoUrl && !deletionStatus.logo)) && (
						<FiTrash2
							className="cursor-pointer text-black hover:text-red-600"
							onClick={handleLogoDelete}
						/>
					)}
				</div>
			</div>

			<ColorPicker
				themeType={theme.type}
				selectedColor={theme.color}
				setSelectedColor={(color) => setTheme((prev) => ({ ...prev, color }))}
				setThemeType={(type: ThemeTypes) =>
					setTheme((prev) => ({ ...prev, type }))
				}
			/>

			{/* Avatar Selection */}
			<div className="mb-8">
				<h3 className="font-medium mb-2">Avatar</h3>
				<p className="text-sm text-gray-500 mb-3">
					Select an avatar from these options or add of your own choice
				</p>

				<div className="grid grid-cols-5 gap-3 border border-dashed border-purple-400 p-4 rounded-md mb-4 max-w-lg">
					{defaultAvatars.map((url, index) => (
						<img
							key={index}
							src={url}
							alt={`Avatar ${index + 1}`}
							className={`w-16 h-16 rounded-full cursor-pointer border-2 ${
								avatar.defaultUrl === url
									? 'border-purpleBrand-500'
									: 'border-transparent'
							}`}
							onClick={() => {
								setAvatar({ customFile: null, defaultUrl: url });
								setDeletionStatus((prev) => ({ ...prev, avatar: false }));
							}}
						/>
					))}
				</div>

				<div className="flex items-center gap-3">
					<label className="relative cursor-pointer">
						<input
							type="file"
							className="hidden"
							onChange={(e) => {
								setAvatar({
									defaultUrl: null,
									customFile: e.target.files?.[0] || null,
								});
								setDeletionStatus((prev) => ({ ...prev, avatar: false }));
							}}
						/>
						<div className="rounded-md border border-blue-100 bg-blue-50 text-gray-600 flex justify-center items-center">
							<div className="flex items-center gap-2 text-sm px-3 py-1">
								<FiImage className="text-blue-600" />
								{avatarFileName}
							</div>
						</div>
					</label>

					{(avatar.customFile ||
						(avatar.defaultUrl && !deletionStatus.avatar) ||
						(!avatar.defaultUrl &&
							store?.avatarUrl &&
							!deletionStatus.avatar)) && (
						<FiTrash2
							className="cursor-pointer text-black hover:text-red-600"
							onClick={handleAvatarDelete}
						/>
					)}
				</div>
			</div>

			{/* Save Button */}
			<div className="flex mt-10 mb-6">
				<button
					onClick={handleSave}
					disabled={isPending}
					className={`${
						isPending
							? 'opacity-50 cursor-not-allowed'
							: 'hover:bg-purpleBrand-600'
					} bg-purpleBrand-500 text-white px-4 py-2 rounded-lg transition`}
				>
					{isPending ? 'Saving...' : 'Save Changes'}
				</button>
			</div>
		</div>
	);
};

export default CustomizeChatbot;
